import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    createOperatingCapabilityRatio,
    deleteOperatingCapabilityRatio,
} from '@/modules/Report/components/BusinessPerformance/services'
import {
    OperatingCapabilityRatio,
    OperatingCapabilityRatioCreateParams,
} from '@/modules/Report/components/BusinessPerformance/types'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem } from 'vnetwork-ui'
import * as yup from 'yup'

export interface RatioFormModalProps extends ModalProps {
    refetch?: () => void
    editData?: OperatingCapabilityRatio | null
    mode: 'create' | 'edit'
}

interface FormData {
    report_year: number
    report_month: number
    total_asset_turnover: number
    inventory_turnover: number
    receivables_turnover: number
    current_asset_turnover: number
    notes?: string
}

const currentYear = dayjs().year()
const RatioFormModal: React.FC<RatioFormModalProps> = ({
    open,
    onClose,
    refetch,
    editData,
    mode,
}) => {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    // Form schema
    const schema = useMemo(
        () =>
            yup.object().shape({
                total_asset_turnover: yup
                    .number()
                    .min(0, t('must_be_positive'))
                    .required(t('required')),
                inventory_turnover: yup
                    .number()
                    .min(0, t('must_be_positive'))
                    .required(t('required')),
                receivables_turnover: yup
                    .number()
                    .min(0, t('must_be_positive'))
                    .required(t('required')),
                current_asset_turnover: yup
                    .number()
                    .min(0, t('must_be_positive'))
                    .required(t('required')),
                notes: yup.string(),
            }),
        [t]
    )

    // Default values
    const defaultValues: FormData = useMemo(
        () => ({
            report_year: currentYear,
            report_month: dayjs().month() + 1,
            total_asset_turnover: 0,
            inventory_turnover: 0,
            receivables_turnover: 0,
            current_asset_turnover: 0,
            notes: '',
        }),
        []
    )

    const formControl = useForm<FormData>({
        defaultValues,
        // @ts-ignore
        resolver: yupResolver(schema),
    })

    const { control, handleSubmit, reset } = formControl

    // Reset form when dialog opens/closes or editData changes
    useEffect(() => {
        if (open) {
            if (mode === 'edit' && editData) {
                const [year, month] = editData.report_month.split('-')
                reset({
                    report_year: parseInt(year || '0'),
                    report_month: parseInt(month || '0'),
                    total_asset_turnover: editData.total_asset_turnover,
                    inventory_turnover: editData.inventory_turnover,
                    receivables_turnover: editData.receivables_turnover,
                    current_asset_turnover: editData.current_asset_turnover,
                    notes: editData.notes || '',
                })
            } else {
                reset(defaultValues)
            }
        }
    }, [open, editData, mode, reset, defaultValues])

    const onSubmit = async (values: FormData) => {
        try {
            setIsSubmitting(true)

            const payload: OperatingCapabilityRatioCreateParams = {
                report_month: `${values.report_year}-${values.report_month}`,
                total_asset_turnover: values.total_asset_turnover,
                inventory_turnover: values.inventory_turnover,
                receivables_turnover: values.receivables_turnover,
                current_asset_turnover: values.current_asset_turnover,
                notes: values.notes || '',
            }

            // Upsert (create or update based on report_month)
            await createOperatingCapabilityRatio(payload)

            // Invalidate queries
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.REPORTS_BI_OPERATING_CAPABILITY_RATIOS],
            })

            // Show success message
            toast.success(
                mode === 'edit' ? t('update-success') : t('create-success')
            )

            // Refetch and close
            refetch?.()
            onClose()
        } catch (error) {
            toast.error(t('something went wrong'))
            console.error('Failed to save operating capability ratio:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleDelete = async () => {
        if (!editData) return

        const confirmed = window.confirm(
            t('confirm_delete_operating_capability_ratio')
        )
        if (!confirmed) return

        try {
            setIsDeleting(true)

            await deleteOperatingCapabilityRatio({
                report_month: `${editData.report_month}`,
            })

            // Invalidate queries
            queryClient.invalidateQueries({
                queryKey: [QUERY_KEYS.REPORTS_BI_OPERATING_CAPABILITY_RATIOS],
            })

            // Show success message
            toast.success(t('delete-success'))

            // Refetch and close
            refetch?.()
            onClose()
        } catch (error) {
            toast.error(t('something went wrong'))
            console.error('Failed to delete operating capability ratio:', error)
        } finally {
            setIsDeleting(false)
        }
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={
                mode === 'edit'
                    ? t('edit_operating_capability_ratio')
                    : t('create_operating_capability_ratio')
            }
            maxWidth="md"
        >
            <form
                onSubmit={handleSubmit(onSubmit as any)}
                className="space-y-4 p-4"
            >
                {/* Report Month */}
                <FormItem
                    control={control}
                    name="report_year"
                    type="select"
                    label={t('report_year')}
                    options={Array.from({ length: 12 }, (_, index) => ({
                        label: `${t('year')} ${currentYear - index}`,
                        value: currentYear - index,
                    }))}
                    allowClear={false}
                    disabled={mode === 'edit'}
                />
                <FormItem
                    control={control}
                    name="report_month"
                    type="select"
                    label={t('report_month')}
                    options={Array.from({ length: 12 }, (_, index) => ({
                        label: `${t('month')} ${index + 1}`,
                        value: index + 1,
                    }))}
                    allowClear={false}
                    placeholder="YYYY-MM (VD: 2024-12)"
                    disabled={mode === 'edit'}
                />
                <FormItem
                    control={control}
                    name="total_asset_turnover"
                    type="number"
                    label={t('total_asset_turnover')}
                    placeholder={t('enter_total_asset_turnover')}
                />
                <FormItem
                    control={control}
                    name="inventory_turnover"
                    type="number"
                    label={t('inventory_turnover')}
                    placeholder={t('enter_inventory_turnover')}
                />

                {/* Receivables Turnover */}
                <FormItem
                    control={control}
                    name="receivables_turnover"
                    type="number"
                    label={t('receivables_turnover')}
                    placeholder={t('enter_receivables_turnover')}
                />

                {/* Current Assets Turnover */}
                <FormItem
                    control={control}
                    name="current_asset_turnover"
                    type="number"
                    label={t('current_asset_turnover')}
                    placeholder={t('enter_current_asset_turnover')}
                />

                {/* Notes */}
                <FormItem
                    control={control}
                    name="notes"
                    type="textarea"
                    label={t('notes')}
                    placeholder={t('enter_notes')}
                />

                {/* Actions */}
                <div className="flex justify-between">
                    <div>
                        {mode === 'edit' && (
                            <Button
                                type="button"
                                variant="outlined"
                                color="error"
                                onClick={handleDelete}
                                disabled={isSubmitting || isDeleting}
                                loading={isDeleting}
                            >
                                {t('delete')}
                            </Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button
                            type="button"
                            variant="outlined"
                            onClick={onClose}
                            disabled={isSubmitting || isDeleting}
                        >
                            {t('cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={isSubmitting || isDeleting}
                            loading={isSubmitting}
                        >
                            {mode === 'edit' ? t('update') : t('create')}
                        </Button>
                    </div>
                </div>
            </form>
        </Dialog>
    )
}

export default RatioFormModal
